import { useGraphQLContext } from "../../context/graphQLContext";
import { useQuery } from "@tanstack/react-query";
import request from "graphql-request";
import {
    getColorCartInfo,
    listCatalogFinishingColors,
    listColorFilters,
    listFilteredColors,
} from "../../queries/colors";

export type Colors = {
    _count: number;
    colors: Array<{ id: string; title: string; url: string }>;
};

export type ColorCatalogEntries<T> = {
    colorCatalogEntries: Array<T>;
};

export type FinishingColors = {
    colors: Array<{
        hue: Array<{
            id: string;
        }>;
    }>;
};

export type ColorCartInfo = {
    chairEntries: Array<{
        variantCost: Array<{
            coverCost: number;
            cover: Array<{
                title: string;
                collection: Array<{
                    patternRepeat: number;
                    shininess: number;
                    id: string;
                    detailInformation: string;
                    title: string;
                    colors: Array<{
                        id: string;
                    }>;
                }>;
            }>;
        }>;
    }>;
};

export type Hue = {
    id: string;
    color: string;
    title: string;
};

export type HueEntries = {
    hueEntries: Array<Hue>;
};

export const useListFilteredColors = (data: {
    color: Array<string>;
    category: string;
    finishing: string;
    coverIds: Array<string>;
    search: string;
}) => {
    const graphqlOptions = useGraphQLContext();

    return useQuery<ColorCatalogEntries<Colors>>({
        queryKey: [
            "catalogColors",
            `${data.color.map((c) => c).join()}${data.category}${data.finishing}`,
            data.coverIds,
            data.search,
        ],
        queryFn: async () => {
            const variables = { ...data, color: data.color.length > 0 ? data.color : null };
            return request({
                ...graphqlOptions,
                document: listFilteredColors,
                variables: variables,
            });
        },
        keepPreviousData: true,
        refetchOnMount: false,
        refetchInterval: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
};

export const useListColorFilters = () => {
    const graphqlOptions = useGraphQLContext();

    return useQuery<HueEntries>({
        queryKey: ["colorFilters"],
        queryFn: async () => {
            return request({
                ...graphqlOptions,
                document: listColorFilters,
            });
        },
        refetchOnMount: false,
        refetchInterval: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
};

export const useListCatalogFinishingColors = (data: { id: Array<string> }) => {
    const graphqlOptions = useGraphQLContext();

    return useQuery<ColorCatalogEntries<FinishingColors>>({
        queryKey: ["catalogFinishingColors", data.id],
        queryFn: async () => {
            const variables = { ...data };
            return request({
                ...graphqlOptions,
                document: listCatalogFinishingColors,
                variables: variables,
            });
        },
        enabled: data.id.length > 0,
        keepPreviousData: true,
        refetchOnMount: false,
        refetchInterval: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
};

export const useGetColorCartInfo = (data: { id: string; cover: string }, enabled: boolean = true) => {
    const graphqlOptions = useGraphQLContext();

    return useQuery<ColorCartInfo>({
        queryKey: ["getColorCartInfo", `${data.id}${data.cover}`],
        queryFn: async () => {
            const variables = { ...data };
            return request({
                ...graphqlOptions,
                document: getColorCartInfo,
                variables: variables,
            });
        },
        enabled: enabled && data.id.length > 0,
        keepPreviousData: true,
        refetchOnMount: false,
        refetchInterval: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
};
